<template>
  <div class="quantity production">
    <!-- Display principal -->
    <div class="displayCalc">
      <div class="number">
        <a>{{ currentValue }}</a>
      </div>
    </div>

    <template v-if="!isDefectSelectionMode">
      <!-- Números 1-9 -->
      <div class="row" v-for="(row, rowIndex) in numberRows" :key="rowIndex">
        <div
          class="column"
          v-for="num in row"
          :key="num"
          @click="appendNumber(num)"
        >
          <div class="number">
            <a>{{ num }}</a>
          </div>
        </div>
      </div>

      <!-- Fila coma / 0 / DEL -->
      <div class="row">
        <div
          class="column comma"
          :class="{
            disabled:
              step === 'fruitsPerTray' ||
              (step === 'weight' && weightHasDecimal) ||
              (step === 'trays' && trayCountHasDecimal),
          }"
          @click="appendDecimal"
        >
          <div class="number"><a>,</a></div>
        </div>
        <div class="column zero" @click="appendNumber(0)">
          <div class="number"><a>0</a></div>
        </div>
        <div class="column del" @click="deleteDigit">
          <div class="number"><a>⌫</a></div>
        </div>
      </div>

      <!-- Fila de acción: AC / OK -->
      <div class="row">
        <div class="column ac" @click="resetCurrent">
          <div class="number"><a>AC</a></div>
        </div>
        <div class="column ok" @click="handleOk">
          <div class="number"><a>OK</a></div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="defects-list">
        <label
          v-for="defect in defectOptions"
          :key="defect"
          class="defect-item"
          :class="{ selected: isDefectSelected(defect) }"
          @click.prevent="toggleDefect(defect)"
        >
          <input
            type="checkbox"
            :checked="isDefectSelected(defect)"
            @change.prevent="toggleDefect(defect)"
          />
          <span>{{ defect }}</span>
        </label>
      </div>

      <div class="row">
        <div class="column ac" @click="resetDefects">
          <div class="number"><a>AC</a></div>
        </div>
        <div class="column ok" @click="handleOk">
          <div class="number"><a>OK</a></div>
        </div>
      </div>
    </template>

    <!-- Confirmación de guardado -->
    <dialog ref="dialog">
      <h2 class="dialog-title">Guardar producción por lote</h2>
      <div class="dialog-content">
        <p>Se va a proceder a guardar los siguientes datos:</p>
        <p><strong>Finca:</strong> {{ farmland.name }}</p>
        <p v-if="zone">
          <strong>Zona:</strong> {{ zone.name }} ({{ zone.code }})
        </p>
        <p><strong>Categoría:</strong> {{ categoryLabel }}</p>
        <p v-if="isDefectSelectionMode">
          <strong>Defectos seleccionados:</strong>
          {{ selectedDefectsDisplay }}
        </p>
        <p v-if="!isDefectSelectionMode && requiresWeight">
          <strong>Peso total:</strong> {{ totalWeightKg }} g
        </p>
        <p v-if="!isDefectSelectionMode">
          <strong>Frutas por tarrina:</strong> {{ fruitsPerTray }}
        </p>
        <p v-if="!isDefectSelectionMode">
          <strong>Número de tarrinas:</strong> {{ trayCountStr }}
        </p>
        <p v-if="!isDefectSelectionMode">
          <strong>Frutas estimadas:</strong> {{ estimatedFruitCount }}
        </p>
        <div class="created-on-field">
          <label for="createdOnInput">
            <strong>Fecha y hora:</strong>
          </label>
          <input
            id="createdOnInput"
            v-model="createdOn"
            type="datetime-local"
            class="datetime-input"
          />
        </div>
      </div>
      <div class="icon-cantidad">
        <div class="icon-wrapper">
          <img
            :src="cancelIcon"
            alt="Cancelar"
            class="icon cancel"
            @click.prevent="closeDialog"
          />
          <p class="icon-label">Cancelar</p>
        </div>
        <div class="icon-wrapper">
          <img
            :src="okeyIcon"
            alt="Confirmar"
            class="icon okey"
            @click.prevent="confirmSave"
          />
          <p class="icon-label">Confirmar</p>
        </div>
      </div>
    </dialog>

    <!-- Resultado del guardado con ícono -->
    <dialog ref="resultDialog">
      <div class="result-dialog-content">
        <img
          :src="resultDialog.success ? successIcon : errorIcon"
          :alt="resultDialog.success ? 'Éxito' : 'Error'"
          class="result-icon"
        />
        <h2 class="dialog-title">{{ resultDialog.message }}</h2>
        <p
          v-if="resultDialog.warning === 'duplicate_same_day'"
          class="dialog-warning"
        >
          ⚠️ Ya existe otro registro hoy con la misma Finca, Zona y Categoría.
        </p>
        <div v-if="resultDialog.success" class="result-dialog-buttons">
          <button @click="acceptAndGoHome" class="result-button accept-button">
            Aceptar
          </button>
          <!-- <button @click="goToMode" class="result-button mode-button">
            Volver a Modo
          </button> -->
          <button @click="goToCategory" class="result-button category-button">
            Seguir contando
          </button>
        </div>
      </div>
    </dialog>
  </div>
</template>

<script>
import errorIcon from "@/assets/icon/advertencia.png";
import cancelIcon from "@/assets/icon/cancel.png";
import successIcon from "@/assets/icon/exito.png";
import okeyIcon from "@/assets/icon/okey.png";

const DEFAULT_OTROS_DEFECTOS = [
  "Blanda",
  "Sangrante",
  "Rota",
  "Deforme",
  "Pétalo pegado",
  "Cicatriz",
];

export default {
  name: "LoteWidget",

  props: {
    farmland: { type: Object, required: true },
    zone: { type: Object, required: false, default: null },
    category: { type: String, required: true },
    modoObj: { type: Object, required: false, default: null },
    categoriaObj: { type: Object, required: false, default: null },
  },

  data() {
    return {
      // step: 'weight' -> peso total (kg), 'fruitsPerTray' -> frutas/tarrina, 'trays' -> nº tarrinas
      step: "weight",
      totalWeightStr: "0",
      fruitsPerTray: 0,
      trayCountStr: "0",
      selectedDefects: [],
      maxLength: 7,
      cancelIcon,
      okeyIcon,
      successIcon,
      errorIcon,
      createdOn: "",
      resultDialog: {
        success: null,
        message: "",
        warning: null,
      },
    };
  },

  mounted() {
    if (this.isDefectSelectionMode) {
      this.$root.$emit("count-step-title", "Defectos");
      return;
    }

    if (!this.requiresWeight) {
      this.step = "fruitsPerTray";
      this.$root.$emit("count-step-title", "Frutas por tarrina");
    } else {
      this.$root.$emit("count-step-title", "Peso total");
    }
  },

  computed: {
    categoryCode() {
      const value = this.categoriaObj?.code || this.category;
      return String(value || "").toUpperCase();
    },

    categoryLabel() {
      return this.categoriaObj?.name || this.category;
    },

    configuredSubcategories() {
      const raw = this.categoriaObj?.subcategories;
      if (!Array.isArray(raw)) return [];
      return raw
        .map((value) => String(value || "").trim())
        .filter((value) => value);
    },

    defectOptions() {
      if (this.configuredSubcategories.length > 0) {
        return this.configuredSubcategories;
      }
      if (this.categoryCode === "OTROS_DEFECTOS") {
        return DEFAULT_OTROS_DEFECTOS;
      }
      return [];
    },

    isDefectSelectionMode() {
      return this.defectOptions.length > 0;
    },

    selectedDefectsDisplay() {
      return this.selectedDefects.length > 0
        ? this.selectedDefects.join(", ")
        : "Sin defectos";
    },

    requiresWeight() {
      if (this.isDefectSelectionMode) return false;
      if (this.modoObj && this.modoObj.requires_units_weight === false)
        return false;
      return true;
    },

    numberRows() {
      return [
        [7, 8, 9],
        [4, 5, 6],
        [1, 2, 3],
      ];
    },

    currentValue() {
      if (this.isDefectSelectionMode) {
        return `${this.selectedDefects.length} defecto(s)`;
      }
      if (this.step === "weight") return this.totalWeightStr;
      if (this.step === "fruitsPerTray") return this.fruitsPerTray;
      return this.trayCountStr;
    },

    weightHasDecimal() {
      return this.totalWeightStr.includes(",");
    },

    trayCountHasDecimal() {
      return this.trayCountStr.includes(",");
    },

    totalWeightKg() {
      return parseFloat(this.totalWeightStr.replace(",", ".")) || 0;
    },

    trayCount() {
      return parseFloat(this.trayCountStr.replace(",", ".")) || 0;
    },

    estimatedFruitCount() {
      return this.fruitsPerTray * this.trayCount;
    },
  },

  methods: {
    isDefectSelected(defect) {
      return this.selectedDefects.includes(defect);
    },

    toggleDefect(defect) {
      if (this.isDefectSelected(defect)) {
        this.selectedDefects = this.selectedDefects.filter(
          (item) => item !== defect,
        );
      } else {
        this.selectedDefects = [...this.selectedDefects, defect];
      }
    },

    resetDefects() {
      this.selectedDefects = [];
    },

    appendNumber(num) {
      if (this.step === "weight") {
        if (this.totalWeightStr === "0" && num === 0) return;
        const newStr =
          this.totalWeightStr === "0"
            ? String(num)
            : this.totalWeightStr + String(num);
        const [intPart, decPart] = newStr.split(",");
        if (decPart !== undefined && decPart.length > 3) return;
        if (intPart.length > 6) return;
        this.totalWeightStr = newStr;
      } else if (this.step === "fruitsPerTray") {
        const nextValue = this.fruitsPerTray * 10 + num;
        if (String(nextValue).length <= this.maxLength) {
          this.fruitsPerTray = nextValue;
        }
      } else {
        if (this.trayCountStr === "0" && num === 0) return;
        const newStr =
          this.trayCountStr === "0"
            ? String(num)
            : this.trayCountStr + String(num);
        const [intPart, decPart] = newStr.split(",");
        if (decPart !== undefined && decPart.length > 2) return;
        if (intPart.length > 6) return;
        this.trayCountStr = newStr;
      }
    },

    resetCurrent() {
      if (this.step === "weight") {
        this.totalWeightStr = "0";
      } else if (this.step === "fruitsPerTray") {
        this.fruitsPerTray = 0;
      } else {
        this.trayCountStr = "0";
      }
    },

    deleteDigit() {
      if (this.step === "weight") {
        if (this.totalWeightStr.length <= 1) {
          this.totalWeightStr = "0";
        } else {
          this.totalWeightStr = this.totalWeightStr.slice(0, -1);
        }
      } else if (this.step === "fruitsPerTray") {
        this.fruitsPerTray = Math.floor(this.fruitsPerTray / 10);
      } else {
        if (this.trayCountStr.length <= 1) {
          this.trayCountStr = "0";
        } else {
          this.trayCountStr = this.trayCountStr.slice(0, -1);
        }
      }
    },

    appendDecimal() {
      if (this.step === "weight" && !this.weightHasDecimal) {
        this.totalWeightStr = this.totalWeightStr + ",";
      } else if (this.step === "trays" && !this.trayCountHasDecimal) {
        this.trayCountStr = this.trayCountStr + ",";
      }
    },

    handleOk() {
      if (this.isDefectSelectionMode) {
        if (this.selectedDefects.length === 0) {
          this.showResultDialog(false, "Debes seleccionar al menos un defecto");
          return;
        }
        this.openDialog();
        return;
      }

      if (this.step === "weight") {
        if (this.totalWeightKg === 0) {
          this.showResultDialog(false, "Peso total inválido");
          return;
        }
        this.step = "fruitsPerTray";
        this.$root.$emit("count-step-title", "Frutas por tarrina");
      } else if (this.step === "fruitsPerTray") {
        if (this.fruitsPerTray === 0) {
          this.showResultDialog(false, "Número de frutas por tarrina inválido");
          return;
        }
        this.step = "trays";
        this.$root.$emit("count-step-title", "Número de tarrinas");
      } else {
        if (this.trayCount === 0) {
          this.showResultDialog(false, "Número de tarrinas inválido");
          return;
        }
        this.openDialog();
      }
    },

    openDialog() {
      // Inicializar con la fecha/hora actual
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      this.createdOn = `${year}-${month}-${day}T${hours}:${minutes}`;
      this.$refs.dialog?.showModal();
    },

    closeDialog() {
      this.$refs.dialog?.close();
    },

    async confirmSave() {
      this.closeDialog();
      try {
        const payload = {
          farmland_id: this.farmland.id,
          production_mode: this.modoObj?.code || "LOTE",
          category: this.category,
        };

        // Añadir fecha de creación si está disponible
        if (this.createdOn) {
          payload.created_on =
            this.createdOn.length === 16
              ? `${this.createdOn}:00`
              : this.createdOn;
        }

        if (this.isDefectSelectionMode) {
          payload.berries_defects = this.selectedDefects;
        } else {
          payload.fruit_count = this.estimatedFruitCount;
          if (this.requiresWeight) {
            payload.units_weight = Math.round(this.totalWeightKg);
          }
        }

        if (this.zone && this.zone.id) {
          payload.zone_id = this.zone.id;
        }

        const response = await this.$http.post(
          `${this.$hostname}/production/`,
          payload,
        );
        console.log("Datos guardados:", response.data);
        this.showResultDialog(
          true,
          "Producción guardada con éxito",
          response.data?.warning || null,
        );
      } catch (err) {
        console.error("Error al guardar producción:", err);
        this.showResultDialog(false, "Error al guardar producción");
      }
    },

    showResultDialog(success, message, warning = null) {
      this.resultDialog.success = success;
      this.resultDialog.message = message;
      this.resultDialog.warning = warning;
      this.$refs.resultDialog?.showModal();

      if (!success) {
        setTimeout(() => {
          this.$refs.resultDialog?.close();
        }, 1500);
      }
    },

    resetState() {
      this.step = this.requiresWeight ? "weight" : "fruitsPerTray";
      this.totalWeightStr = "0";
      this.fruitsPerTray = 0;
      this.trayCount = 0;
      this.selectedDefects = [];
      this.$root.$emit(
        "count-step-title",
        this.isDefectSelectionMode
          ? "Defectos"
          : this.requiresWeight
          ? "Peso total"
          : "Frutas por tarrina",
      );
    },

    acceptAndGoHome() {
      this.$refs.resultDialog?.close();
      this.resetState();
      this.$root.$emit("quantity");
    },

    goToMode() {
      this.$refs.resultDialog?.close();
      this.resetState();
      this.$root.$emit("goto-formats-mode");
    },

    goToCategory() {
      this.$refs.resultDialog?.close();
      this.resetState();
      this.$root.$emit("goto-formats-category");
    },
  },
};
</script>

<style scoped>
/* Filas de las últimas dos líneas usan flex para poder controlar anchos */
.quantity .row:nth-last-child(-n + 2) {
  display: flex !important;
  float: none !important;
}

.column.comma {
  width: 27% !important;
}

.column.comma.disabled {
  opacity: 0.35;
  pointer-events: none;
}

.column.zero {
  width: 27% !important;
}

.column.del {
  width: 27% !important;
}

.column.ac {
  width: 27% !important;
}

.column.ok {
  width: 57% !important;
}

.defects-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 10px 0 14px;
}

.defect-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid rgba(16, 118, 49, 0.25);
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  user-select: none;
}

.defect-item.selected {
  border-color: #107631;
  background: rgba(16, 118, 49, 0.08);
}

.defect-item input {
  width: 16px;
  height: 16px;
}

.result-dialog-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  width: 100%;
}

.result-button {
  padding: 11px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  letter-spacing: 0.01em;
}

/* Botón principal: mismo estilo que ok-button de la app */
.accept-button {
  background: linear-gradient(135deg, #107631 0%, #98cf9c 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 6px rgba(16, 118, 49, 0.25);
}

.accept-button:hover {
  background: linear-gradient(135deg, #0d5e28 0%, #7ab87d 100%);
  box-shadow: 0 6px 10px rgba(16, 118, 49, 0.35);
  transform: translateY(-1px);
}

.accept-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(16, 118, 49, 0.2);
}

/* Botón secundario: contorno verde, fondo blanco */
.mode-button {
  background: white;
  color: #107631;
  border: 1.5px solid #107631;
}

.mode-button:hover {
  background: rgba(16, 118, 49, 0.06);
  border-color: #0d5e28;
  color: #0d5e28;
  transform: translateY(-1px);
}

.mode-button:active {
  transform: translateY(0);
}

/* Botón terciario: neutro gris, tono suave */
.category-button {
  background: rgba(160, 156, 156, 0.15);
  color: #444;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.category-button:hover {
  background: rgba(160, 156, 156, 0.28);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

.category-button:active {
  transform: translateY(0);
}

.dialog-warning {
  font-size: 13px;
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 8px 12px;
  margin: 4px 0 12px;
  text-align: center;
  line-height: 1.4;

  .created-on-field {
    margin-top: 15px;
    padding-top: 10px;
    border-top: 1px solid #e5e5e5;
  }

  .created-on-field label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
  }

  .datetime-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 14px;
    font-family: inherit;
    box-sizing: border-box;
  }

  .datetime-input:focus {
    outline: none;
    border-color: #107631;
    box-shadow: 0 0 0 3px rgba(16, 118, 49, 0.1);
  }
}
</style>
